import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaperPlane, 
  faBell, 
  faUsers, 
  faEye,
  faTrash,
  faEdit,
  faPlus,
  faCalendar,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import '../styles/AdminNotifications.css';

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newNotification, setNewNotification] = useState({
    title: '',
    content: '',
    type: 'info',
    targetAudience: 'all',
    scheduledAt: ''
  });
  const [loading, setLoading] = useState(true);

  // Mock data - In real app, this would come from your backend
  const mockNotifications = [
    {
      id: 1,
      title: 'Cập nhật hệ thống',
      content: 'Hệ thống sẽ bảo trì từ 2:00 - 4:00 sáng ngày mai. Vui lòng sắp xếp thời gian xem phim phù hợp.',
      type: 'warning',
      targetAudience: 'all',
      status: 'sent',
      sentAt: '2024-01-20T10:30:00',
      readCount: 245,
      totalRecipients: 1247
    },
    {
      id: 2,
      title: 'Anime mới đã được thêm',
      content: 'Chúng tôi vừa thêm 15 bộ anime mới vào thư viện. Hãy khám phá ngay!',
      type: 'info',
      targetAudience: 'all',
      status: 'sent',
      sentAt: '2024-01-19T15:45:00',
      readCount: 892,
      totalRecipients: 1247
    },
    {
      id: 3,
      title: 'Chúc mừng năm mới',
      content: 'Chúc tất cả thành viên một năm mới an khang, thịnh vượng!',
      type: 'success',
      targetAudience: 'all',
      status: 'scheduled',
      scheduledAt: '2024-01-25T00:00:00',
      readCount: 0,
      totalRecipients: 1247
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setNotifications(mockNotifications);
      setLoading(false);
    }, 1000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateNotification = () => {
    if (!newNotification.title || !newNotification.content) {
      alert('Vui lòng nhập đầy đủ tiêu đề và nội dung');
      return;
    }

    const notification = {
      id: Date.now(),
      ...newNotification,
      status: newNotification.scheduledAt ? 'scheduled' : 'sent',
      sentAt: newNotification.scheduledAt || new Date().toISOString(),
      readCount: 0,
      totalRecipients: 1247 // Mock total recipients
    };

    setNotifications([notification, ...notifications]);
    setNewNotification({
      title: '',
      content: '',
      type: 'info',
      targetAudience: 'all',
      scheduledAt: ''
    });
    setShowCreateModal(false);
  };

  const handleDeleteNotification = (notificationId) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa thông báo này?')) {
      setNotifications(notifications.filter(n => n.id !== notificationId));
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'success':
        return 'green';
      case 'warning':
        return 'orange';
      case 'error':
        return 'red';
      default:
        return 'blue';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'green';
      case 'scheduled':
        return 'blue';
      case 'draft':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('vi-VN');
  };

  const getReadPercentage = (readCount, totalRecipients) => {
    if (totalRecipients === 0) return 0;
    return Math.round((readCount / totalRecipients) * 100);
  };

  if (loading) {
    return (
      <div className="notifications-loading">
        <div className="loading-spinner"></div>
        <p>Đang tải danh sách thông báo...</p>
      </div>
    );
  }

  return (
    <div className="admin-notifications">
      <div className="notifications-header">
        <div>
          <h2>Quản lý thông báo</h2>
          <p>Gửi thông báo tới thành viên và theo dõi hiệu quả</p>
        </div>
        <button 
          className="create-notification-btn"
          onClick={() => setShowCreateModal(true)}
        >
          <FontAwesomeIcon icon={faPlus} />
          Tạo thông báo mới
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="notification-stats">
        <div className="stat-card blue">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faBell} />
          </div>
          <div className="stat-content">
            <h3>Tổng thông báo</h3>
            <div className="stat-value">{notifications.length}</div>
          </div>
        </div>
        
        <div className="stat-card green">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faPaperPlane} />
          </div>
          <div className="stat-content">
            <h3>Đã gửi</h3>
            <div className="stat-value">
              {notifications.filter(n => n.status === 'sent').length}
            </div>
          </div>
        </div>
        
        <div className="stat-card orange">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faCalendar} />
          </div>
          <div className="stat-content">
            <h3>Đã lên lịch</h3>
            <div className="stat-value">
              {notifications.filter(n => n.status === 'scheduled').length}
            </div>
          </div>
        </div>
        
        <div className="stat-card purple">
          <div className="stat-icon">
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="stat-content">
            <h3>Tổng người nhận</h3>
            <div className="stat-value">1,247</div>
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div className="notifications-list">
        {notifications.map(notification => (
          <div key={notification.id} className="notification-card">
            <div className="notification-header">
              <div className="notification-title">
                <h3>{notification.title}</h3>
                <div className="notification-badges">
                  <span className={`type-badge ${getTypeColor(notification.type)}`}>
                    {notification.type === 'info' ? 'Thông tin' :
                     notification.type === 'success' ? 'Thành công' :
                     notification.type === 'warning' ? 'Cảnh báo' : 'Lỗi'}
                  </span>
                  <span className={`status-badge ${getStatusColor(notification.status)}`}>
                    {notification.status === 'sent' ? 'Đã gửi' :
                     notification.status === 'scheduled' ? 'Đã lên lịch' : 'Nháp'}
                  </span>
                </div>
              </div>
              <div className="notification-actions">
                <button className="action-btn view" title="Xem chi tiết">
                  <FontAwesomeIcon icon={faEye} />
                </button>
                <button className="action-btn edit" title="Chỉnh sửa">
                  <FontAwesomeIcon icon={faEdit} />
                </button>
                <button 
                  className="action-btn delete" 
                  title="Xóa"
                  onClick={() => handleDeleteNotification(notification.id)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
            
            <div className="notification-content">
              <p>{notification.content}</p>
            </div>
            
            <div className="notification-meta">
              <div className="meta-info">
                <span className="meta-item">
                  <FontAwesomeIcon icon={faCalendar} />
                  {notification.status === 'scheduled' 
                    ? `Lên lịch: ${formatDateTime(notification.scheduledAt)}`
                    : `Đã gửi: ${formatDateTime(notification.sentAt)}`
                  }
                </span>
                <span className="meta-item">
                  <FontAwesomeIcon icon={faUsers} />
                  Đối tượng: {notification.targetAudience === 'all' ? 'Tất cả thành viên' : 'Nhóm được chọn'}
                </span>
              </div>
              
              {notification.status === 'sent' && (
                <div className="read-stats">
                  <div className="read-progress">
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ 
                          width: `${getReadPercentage(notification.readCount, notification.totalRecipients)}%` 
                        }}
                      ></div>
                    </div>
                    <span className="progress-text">
                      {notification.readCount}/{notification.totalRecipients} đã đọc 
                      ({getReadPercentage(notification.readCount, notification.totalRecipients)}%)
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create Notification Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Tạo thông báo mới</h3>
              <button 
                className="modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Tiêu đề thông báo *</label>
                <input
                  type="text"
                  value={newNotification.title}
                  onChange={(e) => setNewNotification({
                    ...newNotification,
                    title: e.target.value
                  })}
                  placeholder="Nhập tiêu đề thông báo"
                />
              </div>
              
              <div className="form-group">
                <label>Nội dung *</label>
                <textarea
                  value={newNotification.content}
                  onChange={(e) => setNewNotification({
                    ...newNotification,
                    content: e.target.value
                  })}
                  placeholder="Nhập nội dung thông báo"
                  rows={4}
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Loại thông báo</label>
                  <select
                    value={newNotification.type}
                    onChange={(e) => setNewNotification({
                      ...newNotification,
                      type: e.target.value
                    })}
                  >
                    <option value="info">Thông tin</option>
                    <option value="success">Thành công</option>
                    <option value="warning">Cảnh báo</option>
                    <option value="error">Lỗi</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Đối tượng</label>
                  <select
                    value={newNotification.targetAudience}
                    onChange={(e) => setNewNotification({
                      ...newNotification,
                      targetAudience: e.target.value
                    })}
                  >
                    <option value="all">Tất cả thành viên</option>
                    <option value="admin">Chỉ Admin</option>
                    <option value="moderator">Moderator</option>
                    <option value="member">Thành viên thường</option>
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label>Lên lịch gửi (tùy chọn)</label>
                <input
                  type="datetime-local"
                  value={newNotification.scheduledAt}
                  onChange={(e) => setNewNotification({
                    ...newNotification,
                    scheduledAt: e.target.value
                  })}
                />
                <small>Để trống để gửi ngay lập tức</small>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => setShowCreateModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
                Hủy
              </button>
              <button 
                className="btn-primary"
                onClick={handleCreateNotification}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                {newNotification.scheduledAt ? 'Lên lịch gửi' : 'Gửi ngay'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;